import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  ChatBubbleLeftEllipsisIcon,
  ClockIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useId, useRef, useState } from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Popover } from 'src/modules/shared/components/molecules/popovers/popover';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TemplateSessionNoticeProps } from 'src/modules/communications/types/whatsapp/template-session-notice.types';

/**
 * Explains the template-to-reply sequence without expanding the composer.
 * @param props - Whether the response window expired or a template was sent.
 * @returns Click-activated shared help popover with an ordered visual explanation.
 */
export const WhatsAppWindowHelp = ({
  expired,
  waitingForReply,
}: Pick<TemplateSessionNoticeProps, 'expired' | 'waitingForReply'>) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const setTriggerRef = useCallback((element: HTMLElement | null) => {
    triggerRef.current = element;
  }, []);
  const title = t(
    waitingForReply
      ? 'whatsappTemplates.windowHelpWaitingTitle'
      : expired
        ? 'whatsappTemplates.windowHelpExpiredTitle'
        : 'whatsappTemplates.windowHelpNewTitle'
  );
  const steps = [
    {
      Icon: waitingForReply ? CheckIcon : ChatBubbleLeftEllipsisIcon,
      label: t(
        waitingForReply
          ? 'whatsappTemplates.windowHelpSent'
          : 'whatsappTemplates.windowHelpTemplate'
      ),
      detail: t(
        waitingForReply
          ? 'whatsappTemplates.windowHelpSentDetail'
          : 'whatsappTemplates.windowHelpTemplateDetail'
      ),
      current: !waitingForReply,
      highlighted: true,
    },
    {
      Icon: ArrowUturnLeftIcon,
      label: t('whatsappTemplates.windowHelpReply'),
      current: waitingForReply,
      highlighted: waitingForReply,
    },
    {
      Icon: ClockIcon,
      label: t('whatsappTemplates.windowHelpWindow'),
      current: false,
      highlighted: false,
    },
  ];
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      nativeHandlerRef={setTriggerRef}
      placement="top-end"
      contentId={panelId}
      contentAriaLabel={title}
      contentClassName="w-[min(360px,calc(100vw-24px))] max-h-[calc(100dvh-24px)] overflow-y-auto rounded-xl border-secondary-200"
      handler={
        <Button
          isIconOnly
          appearance={open ? 'soft' : 'ghost'}
          variant={open ? 'whatsapp' : 'secondary'}
          size="xs"
          aria-label={t('whatsappTemplates.windowHelpTitle')}
        >
          <InformationCircleIcon aria-hidden className="h-4 w-4" />
        </Button>
      }
    >
      <div className="p-4">
        <div className="flex items-start gap-2">
          <Text
            as="h3"
            size="paragraph"
            color="primary"
            weight="medium"
            className="flex-1 pt-1"
          >
            {title}
          </Text>
          <Button
            isIconOnly
            appearance="ghost"
            variant="secondary"
            size="xs"
            aria-label={t('whatsappTemplates.windowHelpClose')}
            onClick={() => {
              setOpen(false);
              triggerRef.current?.focus();
            }}
          >
            <XMarkIcon aria-hidden className="h-4 w-4" />
          </Button>
        </div>
        {!waitingForReply && (
          <Text size="tiny" color="secondary" className="mt-1">
            {t(
              expired
                ? 'whatsappTemplates.windowHelpExpired'
                : 'whatsappTemplates.windowHelpNew'
            )}
          </Text>
        )}
        <ol className="mt-4 list-none p-0">
          {steps.map(({ Icon, label, detail, current, highlighted }, index) => (
            <li
              key={label}
              aria-current={current ? 'step' : undefined}
              className="relative flex gap-3 pb-4 last:pb-0"
            >
              {index < steps.length - 1 && (
                <div
                  aria-hidden
                  className="absolute bottom-0 left-4 top-8 border-l border-secondary-200"
                />
              )}
              <div
                aria-hidden
                className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${highlighted ? 'bg-whatsapp-50 text-whatsapp-700' : 'border border-secondary-200 bg-secondary-50 text-secondary-600'}`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 pt-1">
                <Text size="small" weight="medium" color="primary">
                  {label}
                </Text>
                {detail && (
                  <Text size="tiny" color="secondary" className="mt-1">
                    {detail}
                  </Text>
                )}
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-4 flex items-start gap-2 border-t border-secondary-200 pt-3">
          <ArrowPathIcon
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 text-secondary-600"
          />
          <Text size="tiny" color="secondary">
            {t('whatsappTemplates.windowHelpRule')}
          </Text>
        </div>
      </div>
    </Popover>
  );
};
